import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { DecideBreakRequest } from '@runhuman/shared';
import {
  BreakDecisionModal,
  BreakProposalChain,
  SimpleBreakWindowComposer,
  type OfferParams,
  type ScheduleParams,
} from '@runhuman/break-requests-ui';
import {
  useAdminBreakRequestDetail,
  ADMIN_BREAK_REQUEST_DETAIL_QUERY_KEY,
} from '../../use-admin-break-request-detail';
import { ADMIN_OPS_SNAPSHOT_QUERY_KEY } from '../../use-admin-ops-snapshot';
import {
  counterOfferAdminBreakRequest,
  decideAdminBreakRequest,
  listAdminBreakRequestProposals,
} from '../../api';
import { formatBreakWindow } from '../../format-time';
import { useActivitySession } from '../../use-activity-session';
import { discordActivityBreakRequestsTheme } from './discord-activity-break-requests-theme';

interface Props {
  readonly accessToken: string;
  readonly requestId: string;
  readonly onClose: () => void;
}

/**
 * Inline-resolve drawer for an admin break-request row (#4201, expanded for
 * counter-offer parity in #4258, refactored to share the combined decision
 * modal in #4550). Mounted by `<PendingBreakRequestsAlertStrip>` underneath
 * the row whose "Review" button the admin clicked. Mirrors the web admin's
 * `BreakRequestRow` one-for-one: row footer is `[Approve] [Decline] [Counter]`;
 * `[Counter]` opens the shared `<BreakDecisionModal>` (alternatives + custom
 * cross-day window, each row with `[Offer]` / `[Schedule]`). The per-request
 * `<BreakProposalChain>` audit history (#3772) stays under the footer.
 *
 * Drawer detail is a one-shot fetch (no polling) — `useAdminOpsSnapshot`'s
 * 5s tick already drives the strip row in/out, and React Query invalidates
 * the detail cache on every mutation so reopens see fresh data. Counter
 * `Offer` succeeding intentionally does NOT close the drawer: the chain row
 * needs to refresh in place so the admin sees their own offer land in the
 * timeline. `Schedule` and `Approve` are terminal and close the drawer.
 */
export function BreakRequestDrawer({ accessToken, requestId, onClose }: Props) {
  const queryClient = useQueryClient();
  const detail = useAdminBreakRequestDetail(accessToken, requestId);
  const [declineReason, setDeclineReason] = useState('');
  const [declineOpen, setDeclineOpen] = useState(false);
  const [decisionModalOpen, setDecisionModalOpen] = useState(false);

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ADMIN_OPS_SNAPSHOT_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ADMIN_BREAK_REQUEST_DETAIL_QUERY_KEY(requestId) });
  };

  const decideMutation = useMutation({
    mutationFn: (body: DecideBreakRequest) => decideAdminBreakRequest(accessToken, requestId, body),
    onSuccess: () => {
      invalidateAll();
      onClose();
    },
  });

  const counterMutation = useMutation({
    mutationFn: (body: { startsAt: string; endsAt: string; rationale: string | undefined }) =>
      counterOfferAdminBreakRequest(accessToken, requestId, body),
    onSuccess: () => {
      invalidateAll();
      setDecisionModalOpen(false);
    },
  });

  const fetchProposals = useCallback(
    () => listAdminBreakRequestProposals(accessToken, requestId),
    [accessToken, requestId],
  );

  // The viewing admin's Runhuman user id, read from the cached Activity session
  // — drives the timeline's "you" vs the acting admin's name framing (#4999).
  const session = useActivitySession();
  const viewerUserId = session.data?.linkedUser?.userId ?? null;

  // Browser-local IANA tz (the iframe runs in admins' browsers around the
  // world; Slack ops messages stay in ET via server-side formatting).
  const viewerTimezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    [],
  );

  const busy = decideMutation.isPending || counterMutation.isPending;
  const lastError = decideMutation.error ?? counterMutation.error;

  if (detail.isLoading) {
    return (
      <div className="admin-break-drawer admin-break-drawer--loading">Loading request…</div>
    );
  }
  if (detail.error || !detail.data) {
    return (
      <div className="admin-break-drawer">
        <div className="admin-action-error" role="status">
          {detail.error instanceof Error ? detail.error.message : 'Failed to load request'}
        </div>
        <button type="button" className="admin-break-drawer__close" onClick={onClose}>
          Close
        </button>
      </div>
    );
  }

  const { request, decisionOptions, testerAlias } = detail.data;
  // Action gates mirror web admin's `BreakRequestRow`: `proposedBy` is the
  // ball-holder; counter flips it each round, so the same gate works for
  // round 0 and every subsequent round.
  const adminTurn = request.status === 'pending' && request.proposedBy === 'tester';
  const hasCounterRounds = request.proposedBy !== request.originator;
  const windowLabel = formatBreakWindow(request.startsAt, request.endsAt);

  const handleApproveOriginal = () => {
    decideMutation.mutate({ decision: 'approve' });
  };
  const handleDeclineSubmit = () => {
    if (!declineReason.trim()) return;
    decideMutation.mutate({ decision: 'decline', decisionReason: declineReason.trim() });
  };
  const handleOffer = async (params: OfferParams) => {
    await counterMutation.mutateAsync(params);
  };
  const handleSchedule = async (params: ScheduleParams) => {
    await decideMutation.mutateAsync({
      decision: 'approve-alternative',
      alternativeStartsAt: params.startsAt,
      alternativeEndsAt: params.endsAt,
    });
  };

  return (
    <div className="admin-break-drawer" role="dialog" aria-label="Break request detail">
      <header className="admin-break-drawer__header">
        <div className="admin-break-drawer__identity">
          <span className="admin-break-drawer__alias">{testerAlias}</span>
          <span className="admin-break-drawer__window">{windowLabel}</span>
        </div>
        <button
          type="button"
          className="admin-break-drawer__close"
          onClick={onClose}
          aria-label="Close drawer"
        >
          ✕
        </button>
      </header>

      {request.reason && (
        <div className="admin-break-drawer__reason">
          <span className="admin-break-drawer__label">Reason:</span> {request.reason}
        </div>
      )}

      {adminTurn ? (
        <div className="admin-break-drawer__actions">
          <button
            type="button"
            className="admin-break-drawer__approve"
            disabled={busy}
            onClick={handleApproveOriginal}
            title="Approve the originally-requested window now"
          >
            Approve
          </button>
          {declineOpen ? (
            <div className="admin-break-drawer__decline">
              <textarea
                className="admin-break-drawer__decline-input"
                placeholder="Reason for declining (visible to tester)"
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                disabled={busy}
                rows={2}
              />
              <div className="admin-break-drawer__decline-row">
                <button
                  type="button"
                  className="admin-break-drawer__decline-cancel"
                  onClick={() => {
                    setDeclineOpen(false);
                    setDeclineReason('');
                  }}
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="admin-break-drawer__decline-confirm"
                  onClick={handleDeclineSubmit}
                  disabled={busy || !declineReason.trim()}
                >
                  Confirm decline
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="admin-break-drawer__decline-open"
              onClick={() => setDeclineOpen(true)}
              disabled={busy}
            >
              Decline
            </button>
          )}
          <button
            type="button"
            className="admin-break-drawer__counter"
            onClick={() => setDecisionModalOpen(true)}
            disabled={busy}
            title="Offer an alternative window or schedule a custom break"
          >
            Counter
          </button>
        </div>
      ) : (
        <div className="admin-break-drawer__resolved">
          This request is {request.status}. Decisions on resolved rows are made on{' '}
          <code>/admin/schedules</code>.
        </div>
      )}

      <BreakProposalChain
        fetchProposals={fetchProposals}
        hasCounterRounds={hasCounterRounds}
        viewerUserId={viewerUserId}
        timezone={viewerTimezone}
        theme={discordActivityBreakRequestsTheme}
      />

      {lastError && (
        <div className="admin-action-error" role="status">
          {lastError instanceof Error ? lastError.message : 'Action failed'}
        </div>
      )}

      {decisionModalOpen && (
        <BreakDecisionModal
          testerAlias={testerAlias}
          currentWindowLabel={windowLabel}
          decisionOptions={decisionOptions}
          timezone={viewerTimezone}
          busy={busy}
          theme={discordActivityBreakRequestsTheme}
          onOffer={handleOffer}
          onSchedule={handleSchedule}
          onCancel={() => setDecisionModalOpen(false)}
          customWindow={
            <SimpleBreakWindowComposer
              seedStartsAt={request.startsAt}
              seedEndsAt={request.endsAt}
              timezone={viewerTimezone}
              busy={busy}
              theme={discordActivityBreakRequestsTheme}
              onOffer={handleOffer}
              onSchedule={handleSchedule}
            />
          }
        />
      )}
    </div>
  );
}
